import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  CheckCircle,
  XCircle,
  Bell,
  FileText,
  Crosshair,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { Notification, NotificationType } from "../types";

type IconComponent = React.ComponentType<{
  weight?: "fill" | "regular" | "light" | "bold" | "thin" | "duotone";
  className?: string;
  "aria-hidden"?: boolean;
}>;

const typeConfig: Record<
  NotificationType,
  { icon: IconComponent; color: string; weight: "fill" | "regular" }
> = {
  job_done: { icon: CheckCircle, color: "text-green-500", weight: "fill" },
  job_failed: { icon: XCircle, color: "text-destructive", weight: "fill" },
  persona_created: { icon: CheckCircle, color: "text-primary", weight: "fill" },
  decodificacao_done: { icon: Crosshair, color: "text-primary", weight: "fill" },
  roteiro_generated: { icon: FileText, color: "text-primary", weight: "fill" },
  system: { icon: Bell, color: "text-muted-foreground", weight: "regular" },
};

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
}

export function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const config = typeConfig[notification.type] ?? typeConfig.system;
  const Icon = config.icon;

  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
    locale: ptBR,
  });

  return (
    <button
      type="button"
      onClick={() => onClick(notification)}
      className={cn(
        "w-full flex items-start gap-3 px-4 py-3 text-left transition-colors",
        "hover:bg-muted/50 focus-visible:outline-none focus-visible:bg-muted/50",
        !notification.read && "bg-primary/[0.04]",
      )}
    >
      <div className="flex-shrink-0 mt-0.5">
        <Icon
          weight={config.weight}
          className={cn("w-5 h-5", config.color)}
          aria-hidden
        />
      </div>

      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "text-sm leading-snug",
            !notification.read
              ? "font-medium text-foreground"
              : "text-muted-foreground",
          )}
        >
          {notification.title}
        </p>
        {notification.body && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {notification.body}
          </p>
        )}
        <p className="text-xs text-muted-foreground/60 mt-1">{timeAgo}</p>
      </div>

      {!notification.read && (
        <div className="flex-shrink-0 mt-1.5">
          <div className="w-2 h-2 rounded-full bg-primary" aria-label="Não lida" />
        </div>
      )}
    </button>
  );
}
